import {
  PERMISSION_SCORE,
  type PermissionScore,
} from '../../constants/permission.constants';
import type { AuthUserPermission } from '../../models/space.model';

/** Pairs any menu item with the minimum score required to show it. */
export interface ScoredMenuItem<T> {
  item: T;
  minScore?: PermissionScore;
}

/**
 * Returns only the items whose `minScore` is met by `can()`.
 * Items without a `minScore` are always included.
 */
export function filterByScore<T>(
  menuDefinitions: ScoredMenuItem<T>[],
  can: (minScore: PermissionScore) => boolean
): T[] {
  return menuDefinitions
    .filter(({ minScore }) => minScore == null || can(minScore))
    .map(({ item }) => item);
}

/** Minimum-level shorthands accepted by {@link canDo}. */
export type PermissionRequirement =
  | 'can_view'
  | 'can_comment'
  | 'can_edit'
  | 'full_access';

const REQUIREMENT_TO_SCORE: Record<PermissionRequirement, PermissionScore> = {
  can_view: PERMISSION_SCORE.VIEW,
  can_comment: PERMISSION_SCORE.COMMENT,
  can_edit: PERMISSION_SCORE.EDIT,
  full_access: PERMISSION_SCORE.FULL_ACCESS,
};

/**
 * Returns `true` when the given resource permission meets the required level.
 * Higher scores subsume lower scores (FULL_ACCESS satisfies all). Use this
 * in template / menu guards across the table management surface.
 */
export function canDo(
  permission: AuthUserPermission | null | undefined,
  required: PermissionRequirement
): boolean {
  const score = permission?.score;
  if (score == null) return false;
  return score >= REQUIREMENT_TO_SCORE[required];
}
